import { useState } from "react"

import {
  useAdNetworks,
  useUpdateUserNetworks,
  type AdminUser,
} from "@/api/hooks/admin"
import { networkLabel } from "@/lib/networkAccess"
import { showToast } from "@/lib/toast"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

interface Props {
  user: AdminUser
  onClose: () => void
}

function initialSelection(user: AdminUser): Set<string> {
  return new Set((user.network_slugs ?? ["roots"]).map((s) => s.toLowerCase()))
}

export default function UserNetworksModal({ user, onClose }: Props) {
  const { data: networksData } = useAdNetworks()
  const updateNetworks = useUpdateUserNetworks()
  const networks = (networksData?.data ?? []).filter((n) => n.is_enabled)

  const [selected, setSelected] = useState<Set<string>>(() => initialSelection(user))

  // Re-seed the selection if the modal is reused for a different user
  const [prevUser, setPrevUser] = useState(user)
  if (user !== prevUser) {
    setPrevUser(user)
    setSelected(initialSelection(user))
  }

  function toggle(slug: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }

  function handleSave() {
    const slugs = Array.from(selected)
    updateNetworks.mutate(
      { userId: user.id, networkSlugs: slugs },
      {
        onSuccess: () => {
          showToast("User networks updated")
          onClose()
        },
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error updating networks",
            "error",
          ),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[500px] border-[#4a4a4a] bg-[#3a3a3a] text-white">
        <DialogHeader>
          <DialogTitle>Assign networks</DialogTitle>
          <DialogDescription className="text-white">
            Choose which networks {user.username} can see in Ads Manager and
            Offers. Users with no explicit assignment default to Roots only.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {networks.map((network) => {
            const slug = network.slug.toLowerCase()
            const checked = selected.has(slug)
            return (
              <label
                key={network.id}
                className="flex cursor-pointer items-center gap-3 rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 hover:bg-[#353535]"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(slug)}
                  className="h-4 w-4"
                />
                <span className="text-sm font-medium">
                  {network.name || networkLabel(slug)}
                </span>
              </label>
            )
          })}
        </div>

        <div className="flex gap-3 pt-2">
          <button
            onClick={handleSave}
            disabled={updateNetworks.isPending || selected.size === 0}
            className="flex-1 rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda] disabled:opacity-50"
          >
            Save
          </button>
          <button
            onClick={onClose}
            className="flex-1 rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
          >
            Cancel
          </button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
